import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ToastVariant } from './toast.models';

/**
 * A single toast notification. Purely presentational — it renders whatever it is
 * given and emits `dismissed` when the close button is pressed. App code should
 * normally go through `ToastService` + `<app-toast-outlet>` rather than mounting
 * this directly.
 */
@Component({
  selector: 'app-toast',
  templateUrl: './toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent {
  readonly text = input.required<string>();
  readonly variant = input<ToastVariant>('info');

  readonly dismissed = output<void>();

  protected readonly classes = computed(
    () =>
      'animate-toast-in flex w-95 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-lg border border-border-subtle bg-background py-3 pr-3 pl-3.5 shadow-overlay',
  );

  /** A 24px tinted circle carrying the status icon — tone is shown by icon and colour together. */
  protected readonly iconClass = computed(() => {
    const byVariant: Record<ToastVariant, string> = {
      success: 'bg-success-subtle text-success',
      error: 'bg-error-subtle text-error',
      warning: 'bg-warning-subtle text-warning-text',
      info: 'bg-info-subtle text-info',
    };
    return `-mt-px flex size-6 shrink-0 items-center justify-center rounded-pill ${byVariant[this.variant()]}`;
  });

  protected readonly role = computed(() => (this.variant() === 'error' ? 'alert' : 'status'));
}
